import { isProgressBar } from "@src/store/slices/widgets/util";

import {
  ColorFieldDefinition,
  ColorFieldsConfig,
  ColorFieldsSection,
} from "../types";

import { createFieldGroup } from "./fieldFactory";
import { hasFreeShipping, hasGiftWithPurchase } from "./offers";
export const processColorFields = <
  T extends ColorFieldDefinition & { color: string },
>(
  fields: T[],
  sectionTitle: string
): ColorFieldsSection => {
  const filteredFields = fields
    .filter((field) => field.condition)
    .map(({ condition, ...field }) => field);

  return {
    sectionTitle,
    colorFields: filteredFields,
  };
};

export const getQuantityButtonsBackgroundFieldConfigs = (
  quantityButtonVariant: string | undefined,
  offer: any,
  theme: { palette: { common: { black: string; white: string } } }
) => [
  {
    key: "backgroundColors.buttonInactiveBackground",
    label: "Button inactive",
    useOpacity: { defaultColor: theme.palette.common.black, opacity: 0.05 },
  },
  {
    key: "backgroundColors.buttonActiveBackground",
    label: "Button active",
    useOpacity: { defaultColor: theme.palette.common.black, opacity: 0.2 },
  },
  {
    key: "backgroundColors.badgeColor",
    label: "Badge",
    condition: quantityButtonVariant !== "quantityButtonsWidgetCompact",
  },
  {
    key: "backgroundColors.buttonBorderColor",
    label: "Button border",
  },
  {
    key: "backgroundColors.freeShippingBackground",
    label: "Free shipping label",
    condition: hasFreeShipping(offer),
  },
  {
    key: "backgroundColors.freeGiftBackground",
    label: "Free gift label",
    condition: hasGiftWithPurchase(offer),
  },
];

export const getQuantityButtonsColorFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const { quantityButtonVariant, offer, themeColors } = config;

  const fieldConfigs = getQuantityButtonsBackgroundFieldConfigs(
    quantityButtonVariant,
    offer,
    { palette: { common: themeColors } }
  );

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Background");
};

export const getMessageBoxColorFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const fieldConfigs = [
    { key: "container.backgroundColor", label: "Background color" },
  ];

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Background");
};

export const getProgressBarBreakpointFieldConfigs = (
  widget: any,
  theme: { palette: { common: { black: string; white: string } } }
) => [
  {
    key: "breakpoint.inactiveColor",
    label: "Background inactive",
    defaultColor: theme.palette.common.black,
    condition: isProgressBar(widget),
  },
  {
    key: "breakpoint.activeColor",
    label: "Background active",
    defaultColor: theme.palette.common.black,
    condition: isProgressBar(widget),
  },
  {
    key: "breakpoint.inactiveIconColor",
    label: "Icon inactive",
    useContrastText: true,
    useContrastTextOpacity: 0.5,
    condition: isProgressBar(widget),
  },
  {
    key: "breakpoint.activeIconColor",
    label: "Icon active",
    defaultColor: theme.palette.common.white,
    useContrastText: true,
    condition: isProgressBar(widget),
  },
  {
    key: "textColors.breakpointTextInactive",
    label: "Text inactive",
    condition: isProgressBar(widget),
  },
  {
    key: "textColors.breakpointTextActive",
    label: "Text active",
    condition: isProgressBar(widget),
  },
];

export const getProgressBarBreakpointFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const { widget, themeColors } = config;

  const fieldConfigs = getProgressBarBreakpointFieldConfigs(widget, {
    palette: { common: themeColors },
  });

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Breakpoint");
};

export const getProgressBarBarFieldConfigs = (
  widget: any,
  theme: { palette: { common: { black: string; white: string } } }
) => [
  {
    key: "bar.inactiveColor",
    label: "Bar background",
    useOpacity: {
      defaultColor: theme.palette.common.black,
      opacity: 0.5,
    },
    condition: isProgressBar(widget),
  },
  {
    key: "bar.activeColor",
    label: "Bar fill",
    defaultColor: theme.palette.common.black,
    condition: isProgressBar(widget),
  },
];

export const getProgressBarBarFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const { widget, themeColors } = config;

  const fieldConfigs = getProgressBarBarFieldConfigs(widget, {
    palette: { common: themeColors },
  });

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Bar");
};

export const getQuantityButtonsTextColorFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const { quantityButtonVariant, offer } = config;

  const fieldConfigs = [
    { key: "textColors.sectionTitle", label: "Section title" },
    { key: "textColors.buttonTitle", label: "Button title" },
    { key: "textColors.label", label: "Label" },
    {
      key: "textColors.subtitle",
      label: "Subtitle",
      condition: quantityButtonVariant !== "quantityButtonsWidgetCompact",
    },
    { key: "textColors.price", label: "Price" },
    { key: "textColors.comparePrice", label: "Compare at price" },
    {
      key: "textColors.badge",
      label: "Badge",
      useContrastText: true,
      condition: quantityButtonVariant !== "quantityButtonsWidgetCompact",
    },
    {
      key: "textColors.freeShippingLabel",
      label: "Free shipping label",
      useContrastText: true,
      condition: hasFreeShipping(offer),
    },
    {
      key: "textColors.freeGiftLabel",
      label: "Free gift label",
      useContrastText: true,
      condition: hasGiftWithPurchase(offer),
    },
    // {
    //   key: "textColors.freeGiftCompareAtPrice",
    //   label: "Free gift compare at price",
    //   condition: hasGiftWithPurchase(offer),
    // },
  ];

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Text");
};

export const getMessageBoxTextColorFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const fieldConfigs = [
    { key: "container.textColor", label: "Text color", useContrastText: true },
  ];

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Text");
};

export const getProgressBarTextColorFields = (
  config: ColorFieldsConfig
): ColorFieldsSection => {
  const fieldConfigs = [
    { key: "textColors.dynamicProgressText", label: "Text" },
  ];

  const allFields = createFieldGroup(fieldConfigs, config);
  return processColorFields(allFields, "Text");
};
